import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, input, output, signal, ViewChild } from '@angular/core';
import { TranslocoModule } from '@ngneat/transloco';
import { FilterValue } from 'src/openapi-client';
import { SdCheckbox, SdDialog } from '@solid-design-system/components/dist-versioned';
import { Dialog } from 'src/app/common/util/dialog';
import { LoadingIndicatorDirective } from 'src/app/common/directives/loading-indicator.directive';

/** The result returned when a template was stored. */
export interface FilterTemplateResult {
  name: string;
  autoLoad: boolean;
  filterValues: FilterValue[];
}

@Component({
  selector: 'app-save-filter-template-dialog',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [CommonModule, TranslocoModule, LoadingIndicatorDirective],
  templateUrl: './save-filter-template-dialog.component.html',
  styleUrl: './save-filter-template-dialog.component.scss',
})
export class SaveFilterTemplateDialogComponent extends Dialog {
  readonly headline = 'dashboard.saveTemplate.dialog.title';
  readonly nameLabel = 'dashboard.saveTemplate.dialog.name';
  readonly autoLoadLabel = 'dashboard.saveTemplate.dialog.autoLoad';
  readonly activeFiltersHeading = 'dashboard.saveTemplate.dialog.activeFilters';
  readonly cancelLabel = 'dashboard.saveTemplate.dialog.cancel';
  readonly confirmLabel = 'dashboard.saveTemplate.dialog.confirm';

  /** Pre-filled, user-editable name. */
  public readonly name = signal<string>('Vorlage');

  /** Whether the template should be auto-loaded the next time the dashboard opens. */
  public readonly autoLoad = signal<boolean>(false);

  /** Number used to build the "Vorlage N" placeholder. */
  public readonly nextTemplateName = input<number>(1);

  /** Currently active filter selections of the user. */
  public readonly filterValues = input<FilterValue[]>([]);

  /** Emits when the user confirms the dialog with a valid name. */
  public readonly saved = output<FilterTemplateResult>();

  /** Emits when the user cancels the dialog. */
  public readonly cancelled = output<void>();

  protected readonly saving = signal<boolean>(false);

  @ViewChild('dialog')
  private readonly dialog!: ElementRef<SdDialog>;

  @ViewChild('nameInput')
  private readonly nameInput!: ElementRef<HTMLInputElement & { value: string }>;

  @ViewChild('autoLoadInput')
  private readonly autoLoadInput!: ElementRef<SdCheckbox>;

  get canSave(): boolean {
    return this.readName().trim().length > 0 && !this.saving();
  }

  confirm(): void {
    if (!this.canSave) {
      return;
    }
    this.saving.set(true);
    this.saved.emit({
      name: this.readName().trim(),
      autoLoad: this.readAutoLoad(),
      filterValues: this.filterValues(),
    });
    this.saving.set(false);
    this.dialog?.nativeElement?.hide();
  }

  cancel(): void {
    this.cancelled.emit();
  }

  open(): void {
    this.dialog?.nativeElement?.show();
  }

  protected onAfterHide(): void {
    // Host destroys the instance after the dialog is hidden.
  }

  private readName(): string {
    return this.nameInput?.nativeElement?.value ?? this.name();
  }

  private readAutoLoad(): boolean {
    return this.autoLoadInput?.nativeElement?.checked ?? this.autoLoad();
  }
}

